import * as React from "react";
import { CoughSession, DeviceInteractions, DeviceTypes } from "../../misc/api/apiTypes";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import LoadingWithMessage from "../../misc/components/LoadingWithMessage";
import Condition from "../../misc/components/Condition";
import { SetInteractionModalStepAction, SelectSessionsForUploadAction, DevicesAction, SetUploadStatusAction, SetDeviceOverallUploadStatusAction, UploadStatus } from "../DevicesNewReducer";
import { useCurrentStudyConfigurationContext } from "../../Core/CurrentStudyConfigurationContext";
import { useDevicesStateContext } from "../DevicesStateContext";
import { FetchMailAddressAsyncAction } from "./DeviceMailIn";
import { SessionTypes } from "../../misc/api/ApiSessionTypes";
import { Subject } from "webportalclient";
import * as VitaloJak2Api from '../../misc/api/DeviceLinkApi/VitaloJak2';
import * as GenericMessages from "../../misc/api/DeviceLinkApi/GenericMessages";
import { useWebPortalApiClient, useWebPortalLegacyClient } from "../../misc/api/WebPortalTokenClient";
import { ApiDevices } from "../../misc/api/DeviceLinkApi/Devices";
import { faPenToSquare, faTablet, faUser, faDroplet } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

interface SessionSelectionProps {
    dispatch: React.Dispatch<DevicesAction>;
}

export const SessionSelection = ({ dispatch }: SessionSelectionProps) => {
    const { study } = useCurrentStudyConfigurationContext();
    const { upload: { isFetchingSessionCandidates, parentVisitCandidates, parentSessionCandidates }, selectedAction, activeSelectedDevice } = useDevicesStateContext();
    const [selectedOptions, setSelectedOptions] = React.useState<Array<{ id: string; checked: boolean }>>([]);
    const apiClient = useWebPortalApiClient();
    const legacyClient = useWebPortalLegacyClient();
    const [subjects, setSubjects] = React.useState<Subject[]>([]);
    const [recordedCoughSessions, setRecordedCoughSessions] = React.useState<Array<CoughSession>>([]);
    const [fetchingSubjects, setFetchingSubjects] = React.useState<boolean>(false);

    React.useEffect(() => {
        const getSubjects = async () => {
            if (parentVisitCandidates.length !== 0) {
                setFetchingSubjects(true);
                const subjectIds = parentVisitCandidates.map(vc => vc.subjectId);
                const subjectResult = await apiClient.client.getSubjects(undefined, `id in (${subjectIds.join(",")})`);

                setSubjects(subjectResult.value);
                setFetchingSubjects(false);
            } else {
                setSubjects([]);
            }
        }

        getSubjects();
    }, [parentVisitCandidates]);

    React.useEffect(() => {
        const checkForRecordedSessionsFromVJ2 = async () => {
            if (activeSelectedDevice.type === DeviceTypes.VitaloJAK2) {
                const dataCheck = await VitaloJak2Api.ApiCheckData(activeSelectedDevice.id);

                if (parentSessionCandidates.length > 0 || parentVisitCandidates.length > 0 || dataCheck.hasRecordings) {
                    setFetchingSubjects(true);


                    const recordings = await VitaloJak2Api.ApiGetRecordings(activeSelectedDevice.id);

                    if (dataCheck.hasRecordings) {

                        let coughRecordingArray = new Array<CoughSession>();
                        for (const recording of recordings) {

                            const coughSessionByRecordingExternalCode = await (await legacyClient.getCoughSessions([{ Member: 'Id', Values: [recording.externalCode] }]))

                            if (coughSessionByRecordingExternalCode.Count > 0)
                                if (coughSessionByRecordingExternalCode.Data[0].Study.Id === study.id) {
                                    coughRecordingArray.push(coughSessionByRecordingExternalCode.Data[0]);
                                }

                        } setRecordedCoughSessions(coughRecordingArray);
                    }
                    else {
                        setRecordedCoughSessions([]);
                    }
                    setFetchingSubjects(false);
                }
            }
        }

        checkForRecordedSessionsFromVJ2();
    }, [parentSessionCandidates, parentVisitCandidates, activeSelectedDevice.type]);

    const hideUploadButton =
        !isFetchingSessionCandidates &&
        parentVisitCandidates.length < 1 && parentSessionCandidates.length < 1 && recordedCoughSessions.length < 1;

    const handleChange = e => {
        if (selectedOptions.map(x => x.id).includes(e.target.value)) {
            const updatedOptions = selectedOptions.map(x => {
                if (x.id === e.target.value) {
                    return { id: e.target.value, checked: e.target.checked };
                } else {
                    return x;
                }
            });

            setSelectedOptions(updatedOptions);
        } else {
            setSelectedOptions(
                [
                    ...selectedOptions,
                    { id: e.target.value, checked: e.target.checked }
                ]
            );
        }
    };

    const next = async () => {
        const checkDeviceConnection = await ApiDevices();
        if (checkDeviceConnection.length === 0) {
            dispatch(SetUploadStatusAction.create(GenericMessages.FailedToConnectMessage));
            dispatch(SetInteractionModalStepAction.create(5));
            dispatch(SetDeviceOverallUploadStatusAction.create(activeSelectedDevice.id, UploadStatus.Failed));
            return;
        }
        if (!hideUploadButton) {
            const selected = selectedOptions.filter(x => x.checked).map(x => x.id);

            dispatch(
                SelectSessionsForUploadAction.create(
                    selectedOptions.filter(x => x.checked).map(x => x.id)
                )
            );

            switch (selectedAction) {
                case DeviceInteractions.Upload:
                    return dispatch(SetInteractionModalStepAction.create(3));
                case DeviceInteractions.Mail:
                    return await FetchMailAddressAsyncAction.create(apiClient.client, dispatch, parentSessionCandidates, selected, study);
            }
        }
    };

    const back = () => {
        dispatch(SetInteractionModalStepAction.create(1));
    };

    const renderSessionCandidates = () => {
        if (parentSessionCandidates.length > 0 || recordedCoughSessions.length > 0) {
            const bloodGlucoseIcon = <FontAwesomeIcon icon={faDroplet} aria-hidden="true" />;
            const coughIcon = <FontAwesomeIcon icon={faPenToSquare} aria-hidden="true" />;
            if (activeSelectedDevice.type === DeviceTypes.VitaloJAK2) {
                return recordedCoughSessions.map(session => {
                    const icon =
                        session.SessionType.Id === SessionTypes.Cough ? coughIcon : bloodGlucoseIcon;

                    return (
                        <li key={`Candidate${session.Id}`}>
                            <input
                                id={session.Id}
                                type="checkbox"
                                name="action"
                                value={session.Id}
                                checked={
                                    selectedOptions.find(x => x.id === session.Id) !== undefined &&
                                    selectedOptions.find(x => x.id === session.Id).checked
                                }
                                onChange={handleChange}
                            />
                            <label htmlFor={session.Id}>
                                <span className="posh-select-icon">{icon}</span>
                                <span className="posh-select-body">
                                    <span className="posh-select-title">
                                        {session.SessionType.Name} Session
                                    </span>
                                    <span className="posh-select-description text-muted">
                                        <FontAwesomeIcon icon={faUser} aria-hidden="true" />{" "}
                                        {session.Subject.SubjectNumber}&nbsp;
                                        <FontAwesomeIcon icon={faPenToSquare} aria-hidden="true" />{" "}
                                        {session.Visit.Name} • {session.Name}
                                    </span>
                                </span>
                            </label>
                        </li>
                    );
                });
            } else {
                return parentSessionCandidates.map(session => {
                    const icon =
                        session.SessionType.Id === SessionTypes.Cough ? coughIcon : bloodGlucoseIcon;

                    return (
                        <li key={`Candidate${session.Id}`}>
                            <input
                                id={session.Id}
                                type="checkbox"
                                name="action"
                                value={session.Id}
                                checked={
                                    selectedOptions.find(x => x.id === session.Id) !== undefined &&
                                    selectedOptions.find(x => x.id === session.Id).checked
                                }
                                onChange={handleChange}
                            />
                            <label htmlFor={session.Id}>
                                <span className="posh-select-icon">{icon}</span>
                                <span className="posh-select-body">
                                    <span className="posh-select-title">
                                        {session.SessionType.Name} Session
                                    </span>
                                    <span className="posh-select-description text-muted">
                                        <FontAwesomeIcon icon={faUser} aria-hidden="true" />{" "}
                                        {session.Subject.SubjectNumber}&nbsp;
                                        <FontAwesomeIcon icon={faPenToSquare} aria-hidden="true" />{" "}
                                        {session.Visit.Name} • {session.Name}
                                    </span>
                                </span>
                            </label>
                        </li>
                    );
                });
            }
        } else if (parentVisitCandidates.length > 0) {
            const diaryIcon = <FontAwesomeIcon icon={faTablet} aria-hidden="true" />;

            return parentVisitCandidates.map(visit => {
                return (
                    <li key={`Candidate${visit.id}`}>
                        <input
                            id={visit.id}
                            type="checkbox"
                            name="action"
                            value={visit.id}
                            checked={
                                selectedOptions.find(x => x.id === visit.id) !== undefined &&
                                selectedOptions.find(x => x.id === visit.id).checked
                            }
                            onChange={handleChange}
                        />
                        <label htmlFor={visit.id}>
                            <span className="posh-select-icon">{diaryIcon}</span>
                            <span className="posh-select-body">
                                <span className="posh-select-title">
                                    {visit.name} Visit
                                </span>
                                <span className="posh-select-description text-muted">
                                    <FontAwesomeIcon icon={faUser} aria-hidden="true" />{" "}
                                    {subjects.find(s => s.id === visit.subjectId)?.number}
                                </span>
                            </span>
                        </label>
                    </li>
                );
            });
        } else {
            return null;
        }
    };

    const renderSessionCandidateSelection = () => {
        if (isFetchingSessionCandidates || fetchingSubjects) {
            return <LoadingWithMessage message="Retrieving Session Candidates..." />;
        } else if (parentSessionCandidates.length > 0 || parentVisitCandidates.length > 0 || recordedCoughSessions.length > 0) {
            return (
                <>
                    <h2 className="text-primary">Confirm</h2>
                    <p style={{ marginBottom: 15 + "px" }} className="text-muted">
                        Select one or more options to interact with
                    </p>

                    <ul className="posh-select">{renderSessionCandidates()}</ul>
                </>
            );
        } else {
            return (
                <>
                    <h2 className="text-primary">No Sessions Available</h2>
                    <Alert show={true} category={AlertCategoryEnum.Danger}>
                        There are no sessions available for upload.
                        Please ensure device has been configured before attempting to upload
                    </Alert>
                </>
            );
        }
    };

    return (
        <div id="SessionSelection">
            <div className="modal-header bg-primary">
                <h3 className="modal-title">Manage Device</h3>
            </div>
            <div className="modal-body">{renderSessionCandidateSelection()}</div>
            <div className="modal-footer">
                <Condition test={() => !hideUploadButton}>
                    <button
                        className="btn btn-primary"
                        disabled={selectedOptions.filter(o => o.checked).length === 0}
                        onClick={() => next()}
                    >
                        {selectedAction === "DI_MAIL" ? "Next" : "Upload"}
                    </button>
                </Condition>

                <button className="btn btn-primary btn-outline" onClick={back}>
                    Back
                </button>
            </div>
        </div>
    );
}
